/**
 * Plays one span of an <audio> element — "listen to exactly this gap" — with an
 * optional loop. Owns the frame loop that stops (or rewinds) at the span's end;
 * the caller owns the element and how it is started.
 */
export interface ActiveSpan { t0: number; t1: number; loop: boolean; }

export class SpanPlayback {
  private raf = 0;
  active: ActiveSpan | null = null;

  constructor(
    private readonly element: () => HTMLAudioElement | null,
    private readonly play: (el: HTMLAudioElement) => void,
  ) {}

  start(t0: number, t1: number, loop: boolean): void {
    const el = this.element();
    if (!el || t1 <= t0) return;
    this.stop();
    this.active = { t0, t1, loop };
    el.currentTime = t0;
    this.play(el);
    const tick = () => {
      const a = this.active;
      const cur = this.element();
      if (!a || !cur) return;
      if (cur.currentTime >= a.t1) {
        if (a.loop) cur.currentTime = a.t0;
        else { cur.pause(); this.active = null; return; }
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Leave the element where it is; just stop guarding the span. */
  stop(): void {
    cancelAnimationFrame(this.raf);
    this.active = null;
  }
}
